<template>
  <div class="min-h-screen bg-gray-100 p-4">
    <div class="max-w-7xl mx-auto">
      <!-- Cache / freshness bar (hidden) -->
      <div
        v-if="cacheInfo && cacheInfo.fromCache"
        class="bg-white rounded-lg shadow-sm border border-blue-200/70 px-4 py-3 flex items-center justify-between mb-6"
        role="status"
        style="display: none;"
      >
        <div class="flex items-center gap-2 text-sm">
          <span class="material-symbols-outlined text-blue-600">info</span>
          <span class="text-blue-800">
            Data served from cache
            <span class="font-semibold">{{ cacheInfo.cachedMinutesAgo }}</span> minutes ago.
          </span>
        </div>

        <button
          @click="$emit('refresh')"
          :disabled="loading"
          class="inline-flex items-center gap-2 rounded-lg bg-blue-100 px-3 py-1.5 text-xs font-medium text-blue-800
                 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:opacity-60 disabled:cursor-not-allowed"
          aria-label="Refresh trending data"
        >
          <span class="material-symbols-outlined text-sm" :class="{ 'animate-spin': loading }">
            {{ loading ? 'autorenew' : 'refresh' }}
          </span>
          <span>{{ loading ? 'Refreshing…' : 'Refresh' }}</span>
        </button>
      </div>

      <!-- Section header -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-4 flex flex-col md:flex-row items-start md:items-center justify-between mb-6">
        <div class="min-w-0">
          <h2 class="text-2xl md:text-3xl font-extrabold text-primary-dark flex items-center gap-3 flex-wrap drop-shadow-lg">
            <span class="material-symbols-outlined text-primary text-2xl md:text-3xl">trending_up</span>
            <span class="truncate">Trending Videos</span>

            <!-- Category tag -->
            <span
              v-if="computedCategoryLabel"
              class="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-primary/10 to-primary/30 text-primary-dark px-3 py-1 text-sm font-semibold shadow"
              :title="`Category: ${computedCategoryLabel}`"
            >
              <span class="material-symbols-outlined text-primary text-base">category</span>
              {{ computedCategoryLabel }}
            </span>

            <!-- Region tag -->
            <span
              v-if="computedRegionLabel"
              class="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-primary/10 to-primary/30 text-primary-dark px-3 py-1 text-sm font-semibold shadow"
              :title="`Region: ${computedRegionLabel}`"
            >
              <span class="material-symbols-outlined text-primary text-base">location_on</span>
              {{ computedRegionLabel }}
            </span>
          </h2>
        </div>

        <div v-if="totalVideos" class="flex items-center gap-2 text-base text-primary-dark font-semibold mt-4 md:mt-0">
          <span class="material-symbols-outlined text-primary text-xl">video_library</span>
          <span>{{ totalVideos }}</span>
        </div>
      </div>

      <!-- Grid -->
      <div v-if="videos && videos.length > 0">
        <ul class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <li
            v-for="video in visibleVideos"
            :key="video.id || video.url"
            class="group bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition hover:-translate-y-1 overflow-hidden flex flex-col"
          >
            <!-- Thumbnail -->
            <a
              :href="video.url"
              target="_blank"
              rel="noopener noreferrer"
              class="relative block rounded-t-lg overflow-hidden"
              :aria-label="`Open ${video.title} in new tab`"
            >
              <div class="relative aspect-[16/9]">
                <img
                  :src="video.thumbnail"
                  :alt="video.title"
                  class="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.04]"
                  loading="lazy"
                />
                <div
                  class="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/35 via-black/0 to-transparent
                         opacity-0 group-hover:opacity-100 transition-opacity"
                />
                <div
                  class="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0
                         group-hover:opacity-100 transition-opacity"
                >
                  <span class="material-symbols-outlined text-white text-6xl drop-shadow">play_circle</span>
                </div>
              </div>
            </a>

            <!-- Card body -->
            <div class="flex-1 flex flex-col p-6">
              <a :href="video.url" target="_blank" rel="noopener noreferrer" class="block">
                <h3 class="text-base font-bold text-gray-900 leading-snug mb-2">
                  {{ video.title }}
                </h3>
              </a>

              <!-- channel -->
              <div class="flex items-center gap-2 text-sm text-gray-600 mb-2">
                <div class="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center">
                  <span class="material-symbols-outlined text-primary text-base">person</span>
                </div>
                <p class="truncate font-medium" :title="video.channelTitle">{{ video.channelTitle }}</p>
              </div>

              <!-- stats -->
              <div class="flex flex-wrap items-center gap-2 text-xs mb-2">
                <span class="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary-dark px-2 py-1 font-semibold">
                  <span class="material-symbols-outlined text-primary text-sm">visibility</span>
                  {{ formatViews(video.viewCount) }} views
                </span>
                <span class="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary-dark px-2 py-1 font-semibold">
                  <span class="material-symbols-outlined text-primary text-sm">schedule</span>
                  {{ formatDate(video.publishedAt) }}
                </span>
              </div>

              <!-- description -->
              <p v-if="video.description" class="mt-3 text-sm text-gray-700 line-clamp-3">
                {{ video.description }}
              </p>

              <!-- actions -->
              <div class="mt-6 flex items-center justify-between flex-wrap gap-2">
                <div class="flex items-center gap-2">
                  <a
                    :href="video.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex items-center gap-2 rounded-full bg-primary text-white px-4 py-2 text-sm font-semibold
                           hover:bg-primary-dark shadow transition"
                  >
                    <span class="material-symbols-outlined text-base">open_in_new</span>
                    Watch
                  </a>
                  <button
                    type="button"
                    class="inline-flex items-center gap-2 rounded-full bg-gray-100 text-gray-800 px-4 py-2 text-sm font-semibold
                           hover:bg-gray-200 shadow transition"
                    @click="copyLink(video.url)"
                    title="Copy video link"
                  >
                    <span class="material-symbols-outlined text-base">content_copy</span>
                    Copy
                  </button>
                </div>
                <button style="display: none;"
                  type="button"
                  class="inline-flex items-center gap-2 rounded-full bg-green-100 text-green-800 px-4 py-2 text-sm font-semibold
                         hover:bg-green-200 shadow transition"
                  @click="downloadVideoInfo(video)"
                  title="Download video information as text file"
                >
                  <span class="material-symbols-outlined text-base">download</span>
                  Download Info
                </button>
              </div>
            </div>
          </li>
        </ul>

        <!-- Download All Button -->
        <div v-if="visibleCount < videos.length || videos.length > 1" class="flex justify-center mt-6 gap-4">
          <button
            v-if="visibleCount < videos.length"
            @click="showMore"
            class="inline-flex items-center gap-2 rounded-full bg-primary text-white px-6 py-2 text-base font-semibold
                   hover:bg-primary-dark shadow-sm transition"
          >
            <span class="material-symbols-outlined text-base">expand_more</span>
            View More
          </button>
          <button style="display: none;"
            @click="downloadAllVideos"
            class="inline-flex items-center gap-2 rounded-full bg-green-600 text-white px-6 py-2 text-base font-semibold
                   hover:bg-green-700 shadow-sm transition"
            title="Download all visible trending videos info"
          >
            <span class="material-symbols-outlined text-base">download</span>
            Download All ({{ visibleVideos.length }})
          </button>
        </div>
      </div>

      <!-- Empty -->
      <div v-else class="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center flex flex-col items-center">
        <div class="mx-auto mb-4 h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
          <span class="material-symbols-outlined text-primary text-3xl">hourglass_disabled</span>
        </div>
        <p class="text-gray-900 font-bold text-lg">No trending videos for this region/category</p>
        <p class="text-base text-gray-600 mt-2">Try a different category or region and analyze again.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { trendsStore } from '@/store/trends';

export default {
  name: 'TrendsResults',
  props: {
    videos: { type: Array, required: true },
    totalVideos: { type: Number, default: 0 },
    cacheInfo: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    formatViews: { type: Function, default: () => (v) => v },
    formatDate: { type: Function, default: () => (d) => d },
    categoryLabel: { type: String, default: '' },
    regionLabel: { type: String, default: '' }
  },
  emits: ['refresh'],
  data() {
    return {
      visibleCount: 3
    };
  },
  computed: {
    computedCategoryLabel() {
      return this.categoryLabel || trendsStore.getters.getCurrentCategoryName;
    },
    computedRegionLabel() {
      return this.regionLabel || trendsStore.getters.getCurrentRegionName;
    },
    visibleVideos() {
      return this.videos.slice(0, this.visibleCount);
    }
  },
  methods: {
    copyLink(text) {
      if (!text) return;
      navigator.clipboard?.writeText(text).catch(() => {});
    },
    showMore() {
      this.visibleCount = Math.min(this.visibleCount + 3, this.videos.length);
    },

    downloadVideoInfo(video) {
      const content = this.formatVideoForDownload(video);
      const filename = this.sanitizeFilename(`${video.title}_trending_analysis.txt`);
      this.downloadTextFile(content, filename);
    },

    downloadAllVideos() {
      const content = this.visibleVideos.map((video, index) => {
        return `=== TRENDING VIDEO ${index + 1} ===\n\n${this.formatVideoForDownload(video)}\n\n`;
      }).join('\n');

      const regionName = this.computedRegionLabel || 'Global';
      const categoryName = this.computedCategoryLabel || 'All Categories';
      const filename = this.sanitizeFilename(`trending_videos_${regionName}_${categoryName}.txt`);
      
      this.downloadTextFile(content, filename);
    },

    formatVideoForDownload(video) {
      const lines = [
        `TRENDING VIDEO ANALYSIS`,
        `Generated on: ${new Date().toLocaleString()}`,
        `Region: ${this.computedRegionLabel || 'Global'}`,
        `Category: ${this.computedCategoryLabel || 'All Categories'}`,
        '',
        `TITLE: ${video.title}`,
        `CHANNEL: ${video.channelTitle}`,
        `VIEWS: ${this.formatViews(video.viewCount)}`,
        `PUBLISHED: ${this.formatDate(video.publishedAt)}`,
        `URL: ${video.url}`,
        '',
        `DESCRIPTION:`,
        video.description || 'No description available',
        '',
        `ANALYSIS INSIGHTS:`,
        `• This video is currently trending in the selected region`,
        `• View count indicates strong audience engagement`,
        `• Published timing may indicate optimal posting windows`,
        `• Channel authority: ${video.channelTitle} has proven audience reach`,
        '',
        `CONTENT INSPIRATION IDEAS:`,
        `• Create a response or reaction video`,
        `• Develop similar content for your niche`,
        `• Study the title structure and keywords used`,
        `• Analyze the timing strategy for your content calendar`,
        '',
        `KEYWORDS TO CONSIDER:`,
        this.extractKeywords(video.title).join(', '),
        '',
        `---`
      ];

      return lines.join('\n');
    },

    extractKeywords(title) {
      // Simple keyword extraction from title
      const commonWords = ['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'is', 'are', 'was', 'were', 'be', 'been', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should'];
      
      return title
        .toLowerCase()
        .replace(/[^\w\s]/g, '') // Remove punctuation
        .split(' ')
        .filter(word => word.length > 3 && !commonWords.includes(word))
        .slice(0, 8); // Top 8 keywords
    },

    sanitizeFilename(filename) {
      return filename
        .replace(/[^\w\s.-]/g, '') // Remove special characters except dots, hyphens, spaces
        .replace(/\s+/g, '_') // Replace spaces with underscores
        .replace(/_{2,}/g, '_') // Replace multiple underscores with single
        .substring(0, 100); // Limit length
    },

    downloadTextFile(content, filename) {
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(url);

      // Show success message
      this.$emit('show-alert', {
        message: `Downloaded: ${filename}`,
        type: 'success'
      });
    }
  }
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.shadow-sm {
  box-shadow: 0 2px 8px 0 rgba(80,36,255,0.06);
}
.shadow-md {
  box-shadow: 0 4px 16px 0 rgba(80,36,255,0.08);
}
.rounded-lg {
  border-radius: 0.75rem;
}
.border-gray-200 {
  --tw-border-opacity: 1;
  border-color: rgb(229 231 235 / var(--tw-border-opacity));
}
.bg-gray-100 {
  --tw-bg-opacity: 1;
  background-color: rgb(243 244 246 / var(--tw-bg-opacity));
}
</style>
